import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { cn } from '../../lib/cn.js';

/**
 * EmptyState - shown when a list has no items.
 */
export function EmptyState({ icon: Icon, title, description, action, className }) {
    return (
        <div className={cn('flex flex-col items-center justify-center py-16 px-6 text-center', className)}>
            {Icon && (
                <div className="h-12 w-12 rounded-full bg-paper-hover flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-ink-subtle" />
                </div>
            )}
            <h3 className="text-sm font-semibold text-ink">{title}</h3>
            {description && <p className="mt-1.5 text-sm text-ink-subtle max-w-sm">{description}</p>}
            {action && <div className="mt-5">{action}</div>}
        </div>
    );
}

/**
 * PageHeader - standard title block for a page.
 * Includes optional back link, subtitle, and actions on the right.
 */
export function PageHeader({ title, subtitle, backTo, backLabel = 'Back', actions, className }) {
    return (
        <div className={cn('mb-6 sm:mb-8', className)}>
            {backTo && (
                <Link
                    to={backTo}
                    className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted hover:text-ink transition-colors mb-3"
                >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    {backLabel}
                </Link>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0">
                    <h1 className="text-xl sm:text-2xl font-semibold text-ink tracking-tight truncate">
                        {title}
                    </h1>
                    {subtitle && <p className="text-sm text-ink-subtle mt-1">{subtitle}</p>}
                </div>
                {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
            </div>
        </div>
    );
}

/**
 * StatusPill - colour-coded label.
 */
export function StatusPill({ status, children, className }) {
    const variantClasses = {
        success: 'pill-success',
        warning: 'pill-warning',
        danger: 'pill-danger',
        info: 'pill-info',
        neutral: 'pill-neutral',
    };
    return (
        <span className={cn(variantClasses[status] || 'pill-neutral', className)}>
            {children}
        </span>
    );
}

/**
 * LoadingPage - centred spinner for full-page loading.
 */
export function LoadingPage({ message = 'Loading…' }) {
    return (
        <div className="flex flex-col items-center justify-center py-20">
            <svg className="animate-spin h-6 w-6 text-ink-muted" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                <path fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" className="opacity-75" />
            </svg>
            <p className="mt-3 text-sm text-ink-subtle">{message}</p>
        </div>
    );
}

/**
 * ErrorPage - shown when a fetch fails badly.
 */
export function ErrorPage({ message = 'Something went wrong', onRetry }) {
    return (
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <h3 className="text-sm font-semibold text-status-danger">Error</h3>
            <p className="mt-1 text-sm text-ink-muted max-w-md">{message}</p>
            {onRetry && (
                <button onClick={onRetry} className="btn-secondary mt-5 text-sm">
                    Try again
                </button>
            )}
        </div>
    );
}
